import { Link } from 'react-router-dom';
import { useRecovery } from '../context/RecoveryContext';

// Surface pending inbound recovery actions (someone has chosen me as their
// contact and needs me to do something) in the main app shell so the user
// notices without having to dig into /settings/recovery-contacts.
//
// Two states warrant the banner:
//   - PENDING_ACCEPT: they need me to accept the designation
//   - RESET_REQUESTED: they've forgotten their password and need approval
//
// The PENDING_ACCEPT copy names the verification explicitly: the designation
// carries an out-of-band comparison of six security words, and a contact who
// reads "waiting for you to accept" will just click Accept without doing it.
// The words themselves are only ever shown in-app (never in this banner's
// source, an email, or a push) — they must be compared over a channel the
// server does not control.
export function InboundRecoveryBanner(): JSX.Element | null {
  const { inbound, pendingVerifications } = useRecovery();
  const action = inbound.find(
    (r) => r.state === 'PENDING_ACCEPT' || r.state === 'RESET_REQUESTED',
  );

  // Nothing to accept yet, but someone is mid-designation and waiting to
  // compare words right now. This is the only in-app signal during that window:
  // the designation — and so the PENDING_ACCEPT row above — does not exist until
  // they finish. Suppressed once the designation lands, so the two never stack.
  if (!action) {
    const verifying = pendingVerifications.find(
      (v) => !inbound.some((r) => r.protectedUserId === v.protectedUserId),
    );
    if (!verifying) return null;
    return (
      <div className="bg-sky-500/25 border-b border-sky-500/60 text-hbs-text px-4 py-2 flex items-center justify-between">
        <div className="text-sm">
          <strong>Verify security words:</strong>{' '}
          <span>
            {verifying.protectedEmail} is adding you as their recovery contact and is
            waiting to compare security words with you.
          </span>
        </div>
        <Link
          to="/settings/recovery-contacts#compare-security-words"
          id="recovery-verify-banner-compare"
          className="text-sm font-semibold underline hover:text-hbs-primary shrink-0 whitespace-nowrap ml-4"
        >
          Compare words
        </Link>
      </div>
    );
  }

  const isReset = action.state === 'RESET_REQUESTED';
  const message = isReset
    ? `${action.protectedEmail} forgot their safe password and needs your help to reset it.`
    : `${action.protectedEmail} chose you as their recovery contact. Compare security words with them, then accept.`;
  const cta = isReset ? 'Review request' : 'Review and verify';

  return (
    <div className="bg-sky-500/25 border-b border-sky-500/60 text-hbs-text px-4 py-2 flex items-center justify-between">
      <div className="text-sm">
        <strong>{isReset ? 'Recovery request:' : 'Recovery designation:'}</strong>{' '}
        <span>{message}</span>
      </div>
      <Link
        to={isReset ? '/settings/recovery-contacts' : '/settings/recovery-contacts#compare-security-words'}
        className="text-sm font-semibold underline hover:text-hbs-primary shrink-0 whitespace-nowrap ml-4"
      >
        {cta}
      </Link>
    </div>
  );
}
